import { gql } from '@apollo/client';

// Fragment for common importer fields
export const IMPORTER_FIELDS_FRAGMENT = gql`
  fragment ImporterFields on Importer {
    id
    version
    name
    alternateName
    description
    addressList {
      cityName
      country
      countrySubDivision
      countrySubDivisionName
      externalReference {
        id
        customerId
      }
      houseNumber
      houseNumberAddition
      line
      line2
      postCode
      type
      streetName
      postOfficeBox
    }
    phone {
      country
      extension
      number
    }
    email
    pointOfContact {
      email
      name
      phone {
        country
        extension
        number
      }
      title
    }
    legalRepresentative {
      email
      name
      phone {
        country
        extension
        number
      }
      title
    }
    secondaryOfficer {
      email
      name
      phone {
        country
        extension
        number
      }
      title
    }
    identifiers {
      identification
      identificationType
    }
    owners {
      customerId
      type
    }
    businessStructure
    docReadinesses {
      id
      programType
    }
    dpsRecord {
      id
      version
      overallDpsStatus
      dpsStatus {
        code
        time
      }
    }
    memos {
      id
      version
      text
      isPublic
      created {
        time
        client {
          id
          name
        }
      }
      updated {
        time
        client {
          id
          name
        }
      }
      deleted {
        time
      }
    }
  }
`;

export const UPDATE_IMPORTER = gql`
  mutation UpdateImporterRecord(
    $id: ID!
    $input: UpdateImporterInput!
    $version: Int!
  ) {
    updateImporter(id: $id, input: $input, version: $version) {
      record {
        id
        version
      }
    }
  }
`;

// Every field of an ExternalValidationResponse. Shared by the stored-entry
// fragment and the validate query so a re-verified result and the entries it is
// merged with always carry the same fields.
export const EXTERNAL_VALIDATION_RESPONSE_FIELDS_FRAGMENT = gql`
  fragment ExternalValidationResponseFields on ExternalValidationResponse {
    validationResult
    errorMessage
    invalidReason
    companyDetails {
      name
      address {
        cityName
        country
        countrySubDivision
        countrySubDivisionName
        externalReference {
          id
          customerId
        }
        houseNumber
        houseNumberAddition
        line
        line2
        postCode
        postOfficeBox
        streetName
        type
      }
    }
  }
`;

// Every field of an ExternalValidation, so the list can be written back through
// updateImporterEntity, which replaces the whole externalValidations array.
export const EXTERNAL_VALIDATION_FIELDS_FRAGMENT = gql`
  ${EXTERNAL_VALIDATION_RESPONSE_FIELDS_FRAGMENT}
  fragment ExternalValidationFields on ExternalValidation {
    identification {
      identification
      identificationType
    }
    response {
      ...ExternalValidationResponseFields
    }
    validationDate
  }
`;

export const VALIDATE_IDENTIFIER_NUMBER = gql`
  ${EXTERNAL_VALIDATION_RESPONSE_FIELDS_FRAGMENT}
  query ValidateIdentifierNumber(
    $identifier: String!
    $identifierType: IdentifierType!
  ) {
    validateIdentifierNumber(
      identifier: $identifier
      identifierType: $identifierType
    ) {
      ...ExternalValidationResponseFields
    }
  }
`;

export const UPDATE_IMPORTER_ENTITY = gql`
  mutation UpdateImporterEntity(
    $id: ID!
    $input: UpdateImporterEntityInput!
    $version: Int!
  ) {
    updateImporterEntity(id: $id, input: $input, version: $version) {
      record {
        id
        version
      }
    }
  }
`;

export const UPDATE_US_IOR = gql`
  mutation UpdateUsIor($id: ID!, $input: UpdateUsIorInput!, $version: Int!) {
    updateUsIor(id: $id, input: $input, version: $version) {
      record {
        id
        version
      }
    }
  }
`;

export const IMPORTER = gql`
  ${IMPORTER_FIELDS_FRAGMENT}
  ${EXTERNAL_VALIDATION_FIELDS_FRAGMENT}
  query ImporterDetail($importerId: ID!) {
    importer(id: $importerId) {
      ...ImporterFields
      dpsRecord {
        id
        version
        overallDpsStatus
        dpsStatus {
          code
          time
        }
        updated {
          time
        }
      }
      usIor {
        id
        version
        status
        industry
        cbpStatus
        sourcePortal
        sourceChannel
        cbp5106RejectionReasons {
          code
          name
        }
        updated {
          time
        }
        businessStructure
        activationStatus
        activations {
          status
          customsBroker {
            id
          }
        }
        iorNumber {
          value
        }
        payerUnitNumber
        pmsStatus
        poaMethod
        addressList {
          cityName
          country
          countrySubDivision
          countrySubDivisionName
          externalReference {
            id
            customerId
          }
          houseNumber
          houseNumberAddition
          line
          line2
          postCode
          postOfficeBox
          streetName
          type
        }
        identifiers {
          identification
          identificationType
        }
        isResident
        isSubmittedBefore
        pointOfContact {
          email
          name
          phone {
            number
            extension
            country
          }
          title
        }
        docReadinessId
        bondApplicationInformation {
          productDescription
          importsFrom
          expectedDutiesRange
          previousAnnualFeesRange
          customBondApplicationStatus
          supplementaryDutyFlagType
        }
        continuousBonds {
          future {
            amount
            effectiveDate
            number
            suretyCode
            typeCode
            renewalDate
            terminationDate
          }
          past {
            amount
            effectiveDate
            number
            suretyCode
            typeCode
            renewalDate
            terminationDate
          }
          present {
            amount
            effectiveDate
            number
            suretyCode
            typeCode
            renewalDate
            terminationDate
          }
        }
        updated {
          time
        }
        dpsRecord {
          id
          version
          overallDpsStatus
          dpsStatus {
            code
            time
          }
          updated {
            time
          }
        }
        documents {
          id
          deleted {
            time
          }
          tags {
            key
            value
          }
          documentTags
        }
        addresses {
          mailing {
            address
            countryCode
            postalCode
            city
            stateCode
          }
          physical {
            address
            countryCode
            postalCode
            city
            stateCode
          }
        }
        operator {
          id
        }
        tags {
          key
          value
        }
        created {
          time
        }
        memos {
          id
          version
          text
          isPublic
          created {
            time
            client {
              id
              name
            }
          }
          updated {
            time
            client {
              id
              name
            }
          }
          deleted {
            time
          }
        }
      }
      importerEntities {
        nodes {
          id
          version
          sourcePortal
          sourceChannel
          addressList {
            cityName
            country
            countrySubDivision
            countrySubDivisionName
            externalReference {
              id
              customerId
            }
            houseNumber
            houseNumberAddition
            line
            line2
            postCode
            postOfficeBox
            streetName
            type
          }
          updated {
            time
          }
          countryCode
          bonds {
            present {
              referenceIdentifier
              typeCode
              amount
              effectiveDate
              renewalDate
              terminationDate
              surety {
                name
                code
              }
            }
            past {
              referenceIdentifier
              typeCode
              amount
              effectiveDate
              renewalDate
              terminationDate
              surety {
                name
                code
              }
            }
            future {
              referenceIdentifier
              typeCode
              amount
              effectiveDate
              renewalDate
              terminationDate
              surety {
                name
                code
              }
            }
          }
          isResident
          isSubmittedBefore
          isEntitledToPostponedVatAccounting
          status
          identifiers {
            identification
            identificationType
          }
          program {
            docReadiness {
              id
            }
          }
          pointOfContact {
            email
            name
            phone {
              number
              extension
              country
            }
            title
            user {
              id
              name
            }
          }
          activations {
            activated {
              time
            }
            status
            externalIorReferences {
              id
              referenceType
              version
              documents {
                documentTag
                externalReference {
                  externalReferenceType
                  externalReferencePartition
                  id
                  version
                }
              }
            }
            customsBroker {
              id
            }
          }
          externalClientReferences
          dpsRecord {
            id
            version
            overallDpsStatus
            dpsStatus {
              code
              time
            }
            updated {
              time
            }
          }
          externalValidations {
            ...ExternalValidationFields
          }
          importer {
            ...ImporterFields
          }
        }
      }
    }
  }
`;
